"use client";
import { useAtom } from "jotai";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth, useI18N, useSearchPageParams } from "common/hooks";
import { bucketUrlPrefix } from "common/config";
import { useDarkMode } from "@/hooks/useDarkMode";
import "react-tooltip/dist/react-tooltip.css";
import {
  drawerMenuOpenAtom,
  onboardingCurrentScreenAtom,
  onboardingScreenAtom,
} from "@/store/atoms";
import { Sheet, SheetContent, SheetTitle } from "@/shadcn/components/ui/sheet";
import GetNavigationPages from "./GetNavigationPages";
import { useCallback, useEffect, useRef, useState } from "react";
import ModalWithAction from "@/components/common/ModalWithAction";
import { ArrowSquareOutIcon } from "@phosphor-icons/react";
import OnboardingScreensComponent from "../auth/modals/Components/OnboardingScreensComponent";
import { trackEvent, createPageAnalytics, resetAnalytics, registerSuperProperties } from "common/analytics";
import { resolveDeviceFormFactor } from "@/analytics/device";
import { entryPageFromCurrentPage } from "@/analytics/entryPage";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";

function HamburgerMenu() {
  const i18n = useI18N();
  const isDark = useDarkMode();
  const navigate = useNavigate({ from: "/" });
  const { logout, user } = useAuth();
  const profileUrl = bucketUrlPrefix + (user as any)?.profile_photo;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setIsOnboardingModalOpen] = useAtom(onboardingScreenAtom);
  const [, setOnboardingCurrentScreen] = useAtom(onboardingCurrentScreenAtom);
  const { showModal } = useAuthModals();

  // Global search params for syncing with BrandsSection navigation
  const { setSearchPageParams } = useSearchPageParams();

  // Assuming`user` is defined somewhere in your scope
  const isLoggedIn = !!user?.name; // This checks if user is logged in

  const pages = GetNavigationPages(
    isLoggedIn,
    setIsModalOpen,
    setIsOnboardingModalOpen,
    setOnboardingCurrentScreen,
  );

  const [drawerMenuOpen, setDrawerMenuOpen] = useAtom(drawerMenuOpenAtom);
  const [liveMessage, setLiveMessage] = useState("");

  // Reset isModalOpen when drawerMenuOpen closes or user logs out
  useEffect(() => {
    if (!drawerMenuOpen) {
      console.log("Drawer closed - resetting isModalOpen to false");
      setIsModalOpen(false);
    }
  }, [drawerMenuOpen]);

  // Debug logging for isModalOpen changes
  useEffect(() => {
    console.log("isModalOpen changed to:", isModalOpen);
  }, [isModalOpen]);

  useEffect(() => {
    if (!user) {
      setIsModalOpen(false);
    }
  }, [user]);

  const toggleDrawerMenu = useCallback(() => {
    setDrawerMenuOpen((drawerMenuOpen) => !drawerMenuOpen);
  }, [setDrawerMenuOpen]);

  const handleHamburgerLogout = useCallback(async () => {
    console.log("handleHamburgerLogout called!");

    // Outcome event — fire while identity is still attached, before resetAnalytics() clears it.
    trackEvent("account_logged_out", {});

    // New centralized analytics: clear identity + super properties on logout, then re-establish
    // the device-level baseline (reset() also wipes product_type/device/dark_mode). auth_state/
    // user_type are re-registered by useAnalyticsSuperProperties once `user` clears.
    resetAnalytics();
    registerSuperProperties({
      product_type: "app",
      device: resolveDeviceFormFactor(),
      dark_mode: !!isDark,
    });

    setIsModalOpen(false);
    toggleDrawerMenu();
    logout();

    //searhc page param tagging_mode to "off"
    setSearchPageParams((prev) => ({
      ...prev,
      tagging_mode: "off",
    }));


    navigate({ to: "/" });
  }, [logout, navigate, toggleDrawerMenu, isDark]);

  const firstFocusHamburgerRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (drawerMenuOpen && firstFocusHamburgerRef.current) {
      console.log("drawerMenuOpen", drawerMenuOpen);
      // Delay focusing to ensure the element is rendered (reduced from 1000ms for better UX)
      setTimeout(() => {
        console.log("drawerMenuOpen focus set");

        firstFocusHamburgerRef.current.focus();
      }, 100);
    }
  }, [drawerMenuOpen]); // Trigger the effect when the drawer menu opens

  // Handle Escape key press to close the drawer
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && drawerMenuOpen) {
        setDrawerMenuOpen(false);
        setLiveMessage("Menu closed");
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [drawerMenuOpen, setDrawerMenuOpen]);

  const handleOpenAuthModal = () => {
    trackEvent("auth_form_opened", {
      entry_point: "hamburger_menu",
      entry_page: entryPageFromCurrentPage(),
    });
    showModal(AuthModalScreen.EnterEmail);
  };

  return (
    <>
      <Sheet
        open={drawerMenuOpen}
        onOpenChange={() => {
          // toggleDrawerMenu();
          setLiveMessage("Menu closed");
        }}
      >
        <SheetContent
          showClose={false}
          side="right"
          className="w-full bg-bg-brand-secondary-default p-4 flex flex-col h-screen overflow-y-auto"
          aria-label="Navigation drawer"
        >
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <div>
            <div className="relative flex items-center py-6">
              <button
                type="button"
                onClick={() => {
                  createPageAnalytics("profile_menu").track("close_action_button", {});
                  toggleDrawerMenu();
                }}
                className="absolute left-0 focus-blue cursor-pointer bg-transparent border-0 p-0"
                aria-label="Close menu"
              >
                {isDark ? (
                  <img
                    src="/images/Close_dark.svg"
                    alt=""
                    aria-hidden="true"
                  />
                ) : (
                  <img
                    src="/images/Close.svg"
                    alt=""
                    aria-hidden="true"
                  />
                )}
              </button>

              <span className="title-s-semibold text-fg-neutral-strong w-full text-center">
                {i18n.get("profile")}
              </span>
            </div>

            {/* Settings Menu Content */}
            <main className="flex flex-col gap-4 bg-bg-brand-secondary-default w-full">
              {!user?.name && (
                <>
                  {/* Login Banner Section */}
                  <section
                    className="flex flex-col gap-4 items-center px-4 py-6 bg-container-brand-minimal"
                    aria-labelledby="login-cta"
                  >
                    <p className="body-base-medium text-fg-neutral-subtle text-center">
                      Log in to save venues, get instant alerts and share your
                      experiences with the community.
                    </p>
                    <button
                      type="button"
                      className="flex items-center justify-center gap-2 min-h-[40px] min-w-[44px] px-3 py-2 bg-bg-brand-primary-default rounded-lg focus-blue hover:bg-bg-brand-primary-hover transition-colors"
                      onClick={
                        isLoggedIn
                          ? () => {
                              console.log(
                                "Logout onClick handler called - setting modal to true",
                              );
                              setIsModalOpen(true);
                            }
                          : () => {
                              handleOpenAuthModal();
                              toggleDrawerMenu();
                            }
                      }
                    >
                      {/* UserIcon Circle Icon */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="white"
                        aria-hidden="true"
                      >
                        <path d="M12 2.25C6.62391 2.25 2.25 6.62391 2.25 12C2.25 17.3761 6.62391 21.75 12 21.75C17.3761 21.75 21.75 17.3761 21.75 12C21.75 6.62391 17.3761 2.25 12 2.25ZM12 6C13.6569 6 15 7.34315 15 9C15 10.6569 13.6569 12 12 12C10.3431 12 9 10.6569 9 9C9 7.34315 10.3431 6 12 6ZM12 19.5C9.51469 19.5 7.30031 18.2344 6 16.3125C6.02812 14.1562 10.5 12.975 12 12.975C13.4906 12.975 17.9719 14.1562 18 16.3125C16.6997 18.2344 14.4853 19.5 12 19.5Z" />
                      </svg>
                      <span className="body-base-medium text-white">
                        Log in or create account
                      </span>
                    </button>
                  </section>
                </>
              )}

              {user?.name && (
                <section className="py-2">
                  <Link
                    to="/your-profile"
                    onClick={toggleDrawerMenu}
                    className="flex items-center gap-4 w-full min-h-[44px] px-4 py-2 bg-bg-brand-secondary-default hover:bg-bg-brand-ghost-hover transition-colors focus-blue"
                    aria-label={`Profile of ${user?.name}`}
                  >
                    {(user as any)?.profile_photo ? (
                      <img
                        className="w-10 h-10 rounded-full"
                        src={profileUrl}
                        alt={`Profile picture of ${user?.name}`}
                      />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-medium select-none"
                        style={{ backgroundColor: user?.profile_color_code ?? "#8751FF" }}
                        aria-label={`Profile initials of ${user?.name}`}
                      >
                        {user?.name?.charAt(0)?.toUpperCase() ?? ""}
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span
                        className="body-base-medium text-fg-neutral-strong"
                        ref={firstFocusHamburgerRef}
                      >
                        {user?.name}
                      </span>
                      <span className="body-s-regular text-fg-neutral-subtle">
                        {i18n.get("manageAccount") || "Manage account"}
                      </span>
                    </div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      className="ml-auto text-fg-neutral-subtle"
                      aria-hidden="true"
                    >
                      <path
                        d="M7.5 15L12.5 10L7.5 5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                </section>
              )}

              {pages.map(({ elements, group, groupToShowForType }, index) => {
                // CheckIcon if this section should be shown
                const shouldShowSection =
                  (groupToShowForType === "loggedin" && isLoggedIn) ||
                  groupToShowForType === "both";

                if (!shouldShowSection) return null;

                // Filter elements that should be shown
                const visibleElements = elements.filter(
                  ({ linkToShowForType }) =>
                    isLoggedIn
                      ? linkToShowForType === "loggedin" ||
                        linkToShowForType === "both"
                      : linkToShowForType === "both",
                );

                if (visibleElements.length === 0) return null;

                return (
                  <section
                    key={index}
                    className="flex flex-col bg-bg-brand-secondary-default"
                    aria-labelledby={`group-${index}`}
                  >
                    <h2
                      id={`group-${index}`}
                      className="body-base-medium text-fg-neutral-subtle px-4"
                    >
                      {group}
                    </h2>

                    <ul className="flex flex-col">
                      {visibleElements.map(
                        (
                          { icon, iconDark, name, path, onClick, publicUrl },
                          elemIndex,
                        ) => {
                          const linkIcon = isDark ? iconDark : icon;
                          const linkTarget = publicUrl ? "_blank" : "_self";
                          const linkPath = publicUrl || path;
                          const publicUrlIcon = publicUrl ? true : false;

                          return (
                            <li
                              key={name}
                              className={
                                elemIndex > 0
                                  ? "border-t border-neutral-100"
                                  : ""
                              }
                            >
                              <Link
                                className="flex items-center gap-4 w-full min-h-[44px] px-4 py-2 bg-bg-brand-secondary-default hover:bg-bg-brand-ghost-hover transition-colors focus-blue"
                                to={linkPath}
                                target={linkTarget}
                                onClick={onClick}
                                aria-label={
                                  publicUrlIcon
                                    ? `Navigate to ${name} (opens in new window)`
                                    : `Navigate to ${name}`
                                }
                              >
                                <img
                                  src={linkIcon}
                                  alt={`${name} icon`}
                                  width="24"
                                  height="24"
                                  aria-hidden="true"
                                />
                                <span className="body-base-medium text-fg-neutral-strong">
                                  {name}
                                </span>
                                {publicUrlIcon && (
                                  <ArrowSquareOutIcon
                                    size={16}
                                    className="ml-auto text-fg-neutral-strong"
                                    aria-hidden="true"
                                  />
                                )}
                              </Link>
                            </li>
                          );
                        },
                      )}
                    </ul>
                  </section>
                );
              })}

              {/* Version Footer */}
              {/* <footer className="py-4">
 <p className="body-base-medium text-fg-neutral-subtle text-center">
 Version xx.xx.xxx
 </p>
 </footer> */}
            </main>

            {user?.name && (
              <div className="flex justify-center px-4 py-4">
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 w-full min-h-[40px] px-3 py-2 bg-bg-brand-primary-default rounded-lg focus-blue hover:bg-bg-brand-primary-hover transition-colors"
                  onClick={handleHamburgerLogout}
                >
                  <span className="body-base-medium text-white">Log out</span>
                </button>
              </div>
            )}
          </div>

          <ModalWithAction
            isModalOpen={isModalOpen}
            type="default"
            actionButtonType="default"
            title={i18n.get("logOut")}
            subtitle={i18n.get("areYouSureYouWishToLogOut")}
            cancelButtonTitle={i18n.get("cancel")}
            actionButtonTitle={i18n.get("logOut")}
            onClose={() => {
              console.log("onClose called");
              setIsModalOpen(false);
            }}
            onClick={handleHamburgerLogout}
            aria-label="Logout confirmation modal"
          />
        </SheetContent>
      </Sheet>
      <div aria-live="assertive" className="sr-only">
        {liveMessage}
      </div>

      <OnboardingScreensComponent onClose={() => {}} />
    </>
  );
}

export default HamburgerMenu;
